import React from 'react';
import ReactDOM from 'react-dom';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {User} from 'constants/UserConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderItemState from 'filestate/FolderItemState';
import FileStates from 'FileStates';
import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as MetadataTabAction from 'metadata/MetadataTabReduxActions';
import MetadataTabState from 'metadata/MetadataTabState';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import notifications from 'notifications';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {getStore} from 'redux-stores/Store';
import * as RegistryValidatorActions from 'registry/RegistryValidatorActions';
import * as IntegrationFieldUtil from 'schemas/fields/integration/IntegrationFieldUtil';
import EntityStore from 'stores/EntityStore';
import {EntityWithText} from 'ui-library/entity-input/EntityInputConstants';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Modal from 'ui-library/modals/Modal';
import {OngoingTaskResponse} from 'util/TaskRunner';
import {Optional, TextAreaChangeEvent} from 'util/Types';

import CreateUpdateReviewModalBody from './CreateUpdateReviewModalBody';
import requireEsignature, {EsignFailureError} from './requireEsignature';
import {ESignatureInfo, Review, ReviewAction} from './ReviewConstants';
import * as ReviewDAO from './ReviewDAO';
import {ReviewUpdatedResponse} from './ReviewDAO';
import * as ReviewUtil from './ReviewUtil';

function openMetadataTabForError(fileId: string, message: string): void {
  FileBrowserActions.openFile(fileId, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onOpen(fileState: any): void {
      fileState.activateTab(MetadataTabState);
    },
  });
  notifications.warn(message, {id: 'CreateUpdateReviewModal:openMetadataTabForError'});
}

async function isSchemaValidForReview(fileId: string): Promise<boolean> {
  const file = FileStates.get().get(fileId);
  if (file && (file as FolderItemState).model().tagSchema() == null) {
    if (!BenchlingConfig.ELN_ENABLE_SCHEMA_REQUIRED_FOR_REVIEW) {
      return true;
    } else {
      openMetadataTabForError(fileId, 'A schema is required before sending for review.');
      return false;
    }
  }

  const errors = await RegistryValidatorActions.validateFileTagsForReview(fileId);
  if (errors.length) {
    IntegrationFieldUtil.sendReviewBlockingAnalytics(errors);
    getStore().dispatch(MetadataTabAction.setReviewBlockedByErrors(fileId, true));
    openMetadataTabForError(fileId, 'Fix schema errors to send for review.');
    return false;
  } else {
    return true;
  }
}

export function getEsigText(action: ReviewAction | null, buttonText: string): string | null {
  if (!BenchlingConfig.ENABLE_ELN_ESIGNATURE) {
    return null;
  }
  let verb;
  switch (action) {
    case ReviewAction.REJECT:
      verb = 'reject this entry';
      break;
    case ReviewAction.SEND_FOR_REVIEW:
      verb = 'send this entry for review';
      break;
    case ReviewAction.ACCEPT:
      verb = 'accept this entry';
      break;
    case ReviewAction.RETRACT:
      verb = 'retract this entry';
      break;
    case ReviewAction.COMMENT:
      verb = 'comment on this review';
      break;
    default:
      // e.g. updating the auditors of a review
      verb = 'authorize this review edit';
  }
  let text = `After clicking "${buttonText}", you will be prompted to enter your password to ${verb} via an electronic signature.`;
  if (BenchlingConfig.ELN_ESIGNATURE_CONFIG && BenchlingConfig.ELN_ESIGNATURE_CONFIG.type === 'SAML') {
    text += ' A pop-up window will appear from your SSO provider to authenticate you.';
  }
  return text;
}

export interface CreateUpdateReviewModalProps {
  auditors: Array<User>;
  fileId: string;
  onUpdate?: (
    _fileId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  review?: Review;
  isSelfReview: boolean;
  errorMessage: string | null;
}

interface State {
  error: string;
  submitting: boolean;
  selectedAuditors: Array<EntityWithText>;
  message: string | null;
}

/*
This modal is used to create (request) reviews.
It is not used to accept, reject, retract, or re-send reviews that already exist -- that is ReviewStatusModal.
*/
class CreateUpdateReviewModal extends React.Component<CreateUpdateReviewModalProps, State> {
  textRef = React.createRef<HTMLTextAreaElement>();

  static close(): void {
    LegacyModalContainer.close(CreateUpdateReviewModal);
  }

  static async open({
    fileId,
    review,
    onUpdate,
    isSelfReview,
  }: {
    fileId: string;
    onUpdate?: (
      _fileId: string,
      review: Review,
      filePermissions: Permissions,
      snapshotTask: OngoingTaskResponse | null
    ) => void;
    review?: Review;
    isSelfReview: boolean;
  }): Promise<void> {
    LoadingScreen.show();

    const errorMessage = ReviewUtil.getReviewBlockingErrorMessage(fileId);

    if (await isSchemaValidForReview(fileId)) {
      let auditors: Array<User> = [];
      if (!isSelfReview) {
        ({auditors} = await ReviewDAO.fetchAuditors(fileId));
      }
      LegacyModalContainer.open(
        CreateUpdateReviewModal,
        <CreateUpdateReviewModal
          fileId={fileId}
          review={review}
          isSelfReview={isSelfReview}
          onUpdate={onUpdate}
          auditors={auditors}
          errorMessage={errorMessage}
        />
      );
    }

    LoadingScreen.hide();
  }

  override state: State = {
    error: '',
    submitting: false,
    selectedAuditors: [],
    message: this.hasReviewAction() ? ReviewUtil.getDefaultReviewComment(this.getReviewAction()) : null,
  };
  _isMounted = false;

  getReviewAction(): ReviewAction {
    if (this.props.isSelfReview) {
      return ReviewAction.ACCEPT;
    }
    // If the entry previously had a review but was retracted due to a review process change (ex: Legacy -> RLC vice versa),
    // we'll want to resend the review with the new auditors that the user fills in.
    return ReviewAction.SEND_FOR_REVIEW;
  }

  // CreateUpdateReview can be used to update auditors which does not result in a ReviewChange, which has no action associated to it.
  hasReviewAction(): boolean {
    const reviewRetractedBySystemChange =
      this.props.review?.auditors.length === 0 && this.props.review.status === ReviewStatus.RETRACTED;
    return this.props.isSelfReview || !this.props.review || reviewRetractedBySystemChange;
  }

  override componentDidMount(): void {
    this._isMounted = true;
    if (this.textRef.current) {
      const textarea = ReactDOM.findDOMNode(this.textRef.current);
      selutil.restoreSelection(textarea, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        start: (textarea as any).value.length,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        end: (textarea as any).value.length,
      });
    }

    let defaultAuditors: Array<User> = [];
    if (this.props.review) {
      defaultAuditors = this.props.review.auditors;
    } else if (this.props.auditors.length === 1) {
      // Only set default auditors if there is exactly 1, otherwise it becomes a
      // long list of auditors that the user needs to edit.
      defaultAuditors = this.props.auditors;
    }
    this.setState({selectedAuditors: this._ensureTextField(defaultAuditors)});
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  _submitReviewChange({
    signatureInfo,
  }: {
    signatureInfo: Optional<ESignatureInfo>;
  }): Promise<ReviewUpdatedResponse> {
    const auditorHandles = this.props.isSelfReview
      ? [EntityStore.currentUser().handle]
      : this.state.selectedAuditors.map((auditor) => auditor.handle);
    const action = this.hasReviewAction() ? this.getReviewAction() : null;
    if (!action) {
      return ReviewDAO.updateReview(this.props.review!.id, signatureInfo, {auditorHandles});
    } else {
      // If the entry previously had a review but was retracted due to a review process change (ex: Legacy -> RLC vice versa),
      // we'll want to resend the review with the new auditors that the user fills in.
      if (this.props.review) {
        return ReviewDAO.createReviewChange(this.props.fileId, this.props.review.id, signatureInfo, {
          auditorHandles,
          message: this.state.message ?? '',
          action,
        });
      }
      return ReviewDAO.createReview(signatureInfo, {
        auditorHandles,
        message: this.state.message,
        fileApiId: this.props.fileId,
        action,
      });
    }
  }

  async handleSubmit(): Promise<void> {
    this.setState({submitting: true});
    try {
      const signatureInfo = await requireEsignature();
      await ReviewUtil.ensureCurrentRuntimeSnapshotPersisted(this.props.fileId);
      const {review, filePermissions, snapshotTask} = await this._submitReviewChange({signatureInfo});
      let successMessage;
      if (this.props.isSelfReview) {
        successMessage = 'Successfully approved entry';
      } else if (!this.props.review) {
        successMessage = 'Successfully created review';
      } else {
        successMessage = 'Successfully updated review';
      }
      notifications.success(successMessage, {duration: 5});

      if (this.props.onUpdate) {
        this.props.onUpdate(this.props.fileId, review, filePermissions, snapshotTask);
      }

      CreateUpdateReviewModal.close();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      assert(error != null, 'Found null error during submit for review.');
      if (error instanceof EsignFailureError) {
        this.setState({error: error.message});
      } else {
        const friendlyError = notifications.getFriendlyError(error);
        if (friendlyError && this._isMounted) {
          this.setState({error: friendlyError});
        } else {
          notifications.notifyError(error);
        }
      }
      if (this._isMounted) {
        this.setState({submitting: false});
      }
    }
  }

  _shouldRenderCommentBox(): boolean {
    return this.hasReviewAction();
  }

  _ensureTextField(entities: Array<EntityWithText | User>): Array<EntityWithText> {
    return entities.map((entity) => ({
      ...entity,
      text: getPartyDisplayString(entity)!,
    }));
  }

  handleCommentChange = (e: TextAreaChangeEvent): void => {
    this.setState({message: e.target.value});
  };

  handleAuditorChange = (entities: Array<EntityWithText>): void => {
    this.setState({
      selectedAuditors: this._ensureTextField(entities),
    });
  };

  override render(): JSX.Element {
    let inFlightButtonTest;
    let staticButtonText;
    let title;
    if (this.props.isSelfReview) {
      inFlightButtonTest = 'Approving...';
      staticButtonText = 'Approve';
      title = 'Approve entry';
    } else if (this.props.review) {
      inFlightButtonTest = 'Updating...';
      staticButtonText = 'Update review';
      title = 'Update review';
    } else {
      inFlightButtonTest = 'Creating...';
      staticButtonText = 'Create review';
      title = 'Create review';
    }
    const modalProps = {
      className: '_test-create-update-review-modal',
      enableRedesign: true,
      title,
      showFooterBorder: false,
      buttons: [
        {
          submit: true,
          text: this.state.submitting ? inFlightButtonTest : staticButtonText,
          disabled: this.state.submitting || this.props.errorMessage != null,
        },
      ],
      closeable: !this.state.submitting,
      onRequestSubmit: async () => {
        await this.handleSubmit();
      },
      onRequestClose: () => {
        CreateUpdateReviewModal.close();
      },
      analyticsModule: ANALYTICS_MODULE.REVIEWS,
      analyticsModalType: 'create_update_review',
      analyticsMetadata: {isSelfReview: this.props.isSelfReview},
    };

    return (
      <Modal {...modalProps}>
        <CreateUpdateReviewModalBody
          ref={this.textRef}
          auditors={this._ensureTextField(this.props.auditors)}
          isSelfReview={this.props.isSelfReview}
          shouldRenderCommentBox={this._shouldRenderCommentBox()}
          reviewAction={this.hasReviewAction() ? this.getReviewAction() : null}
          buttonText={staticButtonText}
          error={this.state.error || this.props.errorMessage || ''}
          selectedAuditors={this.state.selectedAuditors}
          comment={this.state.message}
          handleCommentChange={this.handleCommentChange}
          handleAuditorChange={this.handleAuditorChange}
        />
      </Modal>
    );
  }
}

export default CreateUpdateReviewModal;
